/**
 * OBWWizard — STAFF FORK (ADR-076 §9 copy-fork of the OBW engine).
 *
 * A deliberate copy of portal-subscriber/src/obw/OBWWizard.tsx, scoped to the
 * journeys a Nexa consultant launches from the staff dashboard (BOW today). The
 * engine is COPIED per surface and forked — never shared at runtime (no
 * portal-shared OBW, no iframe of the subscriber portal). Two copies, contained
 * blast radius; behavioural fixes are applied to both.
 *
 * CONTEXT-SEVERANCE (ADR-076 §4): the engine carries NO knowledge of auth,
 * session, route, or portal. It emits results to its mount (the staff modal,
 * StaffObwModal) and never routes:
 *   - L1 mount-owns-exit: required onComplete(result)/onExit(reason) props; no
 *     useNavigate / navigate() anywhere. The staff mount dismisses the modal in
 *     place and toasts on the staff dashboard — it never opens a subscriber tab.
 *   - L2 no native dialogs: exit confirmation is the in-app panel inside
 *     .obw-overlay; never window.confirm / window.close.
 *   - L5 no journeyId sniffing: per-journey behaviour is data-driven.
 *
 * BOW is account-less (accountId ''): persistStep / rollback are inert by
 * construction (they early-return on empty accountId / non-rollback policy), so
 * the engine never touches an account-scoped privileged URL — bow-complete
 * provisions through the HMAC-validated portal-api route (L3 reference shape).
 */
import { useState, useCallback } from 'react';
import './obw.css';
import { JOURNEY_MATRIX, MASTER_STEP_LABELS, type JourneyId, type ObwStepId } from './journeys';
// ADR-107 §2 — the per-step validators. Byte-identical to the subscriber fork's
// copy (asserted by obw-validator-parity.test.ts in both portals).
import { firstIncompleteRequiredStep, type ObwEvidence } from './validators';
import type { ResidencyContext } from './residency';
import { StepResidency } from './steps/StepResidency';

import { BowWelcomeStep } from './steps/BowWelcomeStep';
import { StepWelcome } from './steps/StepWelcome';
import { StepDomainVerify } from './steps/StepDomainVerify';
import { StepSubscribe } from './steps/StepSubscribe';
import { StepPayment } from './steps/StepPayment';
import { StepProfile } from './steps/StepProfile';
import { StepPersona } from './steps/StepPersona';
import { BowCompleteStep } from './steps/BowCompleteStep';
import { SowCompleteStep } from './steps/SowCompleteStep';
import { B2sCompleteStep } from './steps/B2sCompleteStep';
// ADR-076 Option 2 — admin-assisted partner-apply (POW) step renderers.
import { PartnerPickSubscriberStep } from './steps/partner/PartnerPickSubscriberStep';
import { PartnerWelcomeStep } from './steps/partner/PartnerWelcomeStep';
import { PartnerTermsStep } from './steps/partner/PartnerTermsStep';
import { PartnerPrefillConfirmStep } from './steps/partner/PartnerPrefillConfirmStep';
import { PartnerBusinessDetailStep } from './steps/partner/PartnerBusinessDetailStep';
import { PartnerSubmitStep } from './steps/partner/PartnerSubmitStep';
import { PartnerStatusStep } from './steps/partner/PartnerStatusStep';

export interface AnalysisResult {
  accountId: string;
  domainUrl: string;
  heroHeadline: string;
  placeNoun: string;
  audienceNoun: string;
  funnel: Array<{ icon: string; label: string; description: string }>;
  brandTone: string;
  analysedAt: string;
}

export interface StepProps {
  journeyId: JourneyId;
  accountId?: string;
  domainToken?: string;
  actor: 'subscriber' | 'nexa-admin';
  isRequired: boolean;
  data: Record<string, unknown>;
  analysisResult?: AnalysisResult | null;
  onNext: (stepData: Record<string, unknown>) => void;
  /**
   * The next step in the effective sequence, so a step can label its forward button by what
   * ACTUALLY comes next (PL22 — the subscriber fork has carried this since PL22; the staff fork
   * gained it 29 July 2026 when the BOW acquired a plan step).
   *
   * ⚠ WITHOUT IT THE PLAN STEP LIES. Its CTA was `isRequired ? 'Continue to payment' :
   * 'Continue'`, which is right for SOW (plan → payment) and WRONG for the BOW, whose plan step
   * is followed by `profile` and which has no payment step at all — a beta is not charged.
   * Undefined on the last step.
   */
  nextStepId?: ObwStepId;
  /** Jump directly to an earlier step (no-op if not in the journey) — used by
   *  BowCompleteStep to deep-link a duplicate-email/domain failure back to the
   *  editable field rather than dead-ending at the final confirmation. */
  onJumpToStep: (stepId: ObwStepId) => void;
  /** ADR-077 A1.3 — exit the journey immediately (mount routes via onExit). Used
   *  by the POW pick step's continue/restart fork: "Continue" closes the modal
   *  (no data entered, so no discard confirm). The step signals; the mount routes
   *  (L1 severance). Optional: not every step uses it, and test harnesses may omit it. */
  requestExit?: () => void;
  /** ADR-124 I3 — the residency step's mount data + confirm port (see OBWWizardProps). */
  residency?: ResidencyContext;
}

/** Why the wizard exited without completing (L1 — the mount routes on this). */
export type ObwExitReason = 'close' | 'rollback';

/** Emitted to the mount when the journey completes (L1). */
export interface ObwCompletionResult {
  journeyId: JourneyId;
  accountId: string;
  stepData: Record<string, unknown>;
}

interface OBWWizardProps {
  journeyId: JourneyId;
  accountId: string;
  domainToken?: string;
  actor?: 'subscriber' | 'nexa-admin';
  initialStepData?: Partial<Record<ObwStepId, Record<string, unknown>>>;
  /**
   * ADR-107 §2.4 — mount-supplied transport ports, kept in step with the
   * subscriber fork. The staff mount supplies NEITHER (its journeys are
   * account-less; see the note above `triggerAnalysis`), so both are optional and
   * both no-op — which is the engine correctly doing nothing, rather than the
   * engine quietly firing an unauthenticated request nobody could observe.
   */
  onPersistStep?: (stepId: ObwStepId, data: Record<string, unknown>) => Promise<void>;
  onAnalyse?: (domainUrl: string) => Promise<AnalysisResult | null>;
  /** ADR-107 §2.1 — server-side validator evidence. Unused by the account-less
   *  staff mount; present so the two forks share one engine shape. */
  serverEvidence?: Pick<ObwEvidence, 'tier' | 'setupState' | 'domainCount' | 'provisioned'>;
  onComplete: (result: ObwCompletionResult) => void;
  onExit: (reason: ObwExitReason) => void;
  /**
   * ADR-124 I3/D5 — the residency step's mount data and confirm port, kept in step with the
   * subscriber fork. On this fork `confirm` is where the MOUNT mints the launch token in the
   * zone the customer's country derives — a port the mount supplies, so the engine still holds
   * no transport (the zero-network gate stays green).
   */
  residency?: ResidencyContext;
}

export function OBWWizard({
  journeyId,
  accountId,
  domainToken: initialDomainToken,
  actor = 'nexa-admin',
  initialStepData,
  onPersistStep,
  onAnalyse,
  serverEvidence,
  onComplete,
  onExit,
  residency,
}: OBWWizardProps) {
  const journey = JOURNEY_MATRIX[journeyId];

  const seedData = { ...(initialStepData ?? {}) } as Record<ObwStepId, Record<string, unknown>>;

  const [stepIndex, setStepIndex] = useState(0);
  const [stepData, setStepData] = useState<Record<ObwStepId, Record<string, unknown>>>(seedData);
  const [domainToken] = useState<string | undefined>(initialDomainToken);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fatalError, setFatalError] = useState('');
  const [confirmingExit, setConfirmingExit] = useState(false);

  const currentStepId = journey.steps[stepIndex];

  function flatten(map: Record<ObwStepId, Record<string, unknown>>): Record<string, unknown> {
    return Object.values(map).reduce<Record<string, unknown>>((acc, d) => ({ ...acc, ...d }), {});
  }
  function nonEmpty(v: unknown): boolean {
    return typeof v === 'string' && v.trim().length > 0;
  }
  function getStepData(): Record<string, unknown> {
    return flatten(stepData);
  }
  /**
   * ADR-107 §2.1 — evidence as this engine sees it. Kept in step with the
   * subscriber fork; the account-less staff mount supplies no server half, so
   * validation runs on the answers collected in this sitting PLUS the one server
   * fact this journey produces itself.
   *
   * ⭐⭐ THE PROVISION RESULT IS THE SERVER HALF, AND WITHOUT IT THE DONE-GATE WAS
   * UNSATISFIABLE — WHICH IS WHAT THE "LOOPS BACK TO STEP 2" UAT FAILURE WAS.
   *
   * `domain-verify` is validated by a Domain ROW and never by the typed answer —
   * deliberately, because accepting the answer is the silent half of the #313
   * defect (an account finishing setup owning nothing). The subscriber fork mints
   * its Domain mid-journey and counts it (`mintedDomain`). This fork mints it at
   * the very END, inside the completion step's provision call — and nothing fed
   * that back. So `complete()` asked "does this account own a domain?", got
   * `false` from an engine that had just successfully created one, and navigated
   * to `domain-verify` — index 1, which the consultant reads as Step 2. It could
   * not have passed for ANY input: an account-less mount starts at domainCount 0
   * and had no way to leave it.
   *
   * Counting it is reporting a confirmed server write, not assuming one:
   * `provisionedAccountId` is set only from a 2xx provision response, and that
   * route mints the Domain under a uniqueness guard that returns
   * `domain_already_registered` when it cannot. A refused provision writes
   * nothing here, so the gate still bites (pinned by the ops E2E).
   */
  function currentEvidence(map: Record<ObwStepId, Record<string, unknown>>): ObwEvidence {
    const base = serverEvidence ?? { domainCount: 0, provisioned: false };
    const justProvisioned = nonEmpty(flatten(map).provisionedAccountId);
    return {
      answers: map,
      tier: base.tier ?? null,
      setupState: base.setupState ?? null,
      domainCount: (base.domainCount ?? 0) + (justProvisioned ? 1 : 0),
      provisioned: (base.provisioned ?? false) || justProvisioned,
      // A property of the MOUNT, known before any I/O — every staff journey is
      // launched with accountId '' and provisions at its completion step, so the
      // profile step is the only source of the owner's email (see validators.ts).
      accountless: !accountId,
      // ADR-124 — the served map judges the `residency` answer (every staff journey is account-less).
      countryZones: residency?.map?.countries,
    };
  }

  /**
   * ⛔ VALIDATOR-GATED, NOT INDEX-GATED (ADR-107 §2.2) — kept in step with the
   * subscriber fork. Completion used to mean "the step index reached the end of
   * the array", which is a statement about the UI rather than about the work. If
   * a required step is still unvalidated the engine navigates TO it instead of
   * completing: it never completes with work outstanding and never loops.
   *
   * ⚠ FOR THE ACCOUNT-LESS STAFF JOURNEYS THIS VALIDATES THE CONSULTANT'S OWN
   * ANSWERS, which is the right bar here — the terminal provision call is what
   * commits, and it is better to hold the consultant on an unfinished step than
   * to provision a participant from a half-filled journey.
   */
  function complete(map: Record<ObwStepId, Record<string, unknown>>) {
    const outstanding = firstIncompleteRequiredStep(
      journey.steps,
      journey.required,
      currentEvidence(map),
    );
    if (outstanding) {
      const idx = journey.steps.indexOf(outstanding);
      if (idx >= 0) {
        setStepIndex(idx);
        return;
      }
    }
    onComplete({ journeyId, accountId, stepData: flatten(map) });
  }

  // ── ADR-107 §2.4 — THE ENGINE HOLDS NO `fetch` ────────────────────────────────
  //
  // Both of the raw calls that used to live here were UNAUTHENTICATED: no Bearer,
  // against `/portal/obw/*` routes that sit behind the Cognito subscriber
  // `clientAuthorizer`. On the subscriber fork the identical calls 401'd in
  // production for months, silently, because a best-effort fetch inside a bare
  // `try {} catch {}` makes a refused write and a successful one look the same
  // (probed live 22 July 2026 — see the subscriber fork's module header).
  //
  // ⭐ ON THIS FORK THEY WERE DOUBLY DEAD, AND THAT IS THE HONEST FINDING.
  // StaffObwModal mounts every staff journey — bow, sow, b2s, partner-apply —
  // with `accountId=""` (trust is the HMAC launch token, re-verified server-side
  // at the provision/convert route), so `persistStep` early-returned on EVERY
  // call it has ever received. And even had an accountId been supplied, a
  // staff-pool JWT is REJECTED by the client authorizer by design (ADR-018
  // cross-portal isolation), so this fork could not have persisted through these
  // routes at all.
  //
  // ⛔ SO NO STAFF PERSISTENCE ROUTE WAS ADDED, DELIBERATELY. It would need a new
  // `/admin/obw/*` route, a new Cedar action and a CDK/IAM change, to persist
  // resume state for journeys that are consultant-supervised, complete in one
  // sitting, and already carry a token-authorised terminal write. That is scope
  // for no outcome (ADR-107 §2.5).
  //
  // What "kept in step with the subscriber fork" means here is therefore: the
  // same engine SHAPE — mount-supplied ports, no engine-owned transport, the same
  // validator module — with this mount supplying no ports. The durable gate
  // (`staff-obw-no-unauthenticated-calls.test.tsx`) asserts this fork emits ZERO
  // network calls, which is the only form the claim can honestly take for an
  // account-less mount.
  const triggerAnalysis = useCallback(
    async (domainUrl: string) => {
      if (journey.autoFill !== 'website-ai' || !onAnalyse) return;
      setAnalysisLoading(true);
      try {
        const result = await onAnalyse(domainUrl);
        if (result) setAnalysisResult(result);
      } catch {
        // Non-fatal — analysis is pre-fill only.
      } finally {
        setAnalysisLoading(false);
      }
    },
    [journey.autoFill, onAnalyse],
  );

  async function persistStep(stepId: ObwStepId, data: Record<string, unknown>) {
    if (!accountId || !onPersistStep) return;
    try {
      await onPersistStep(stepId, data);
    } catch {
      // Best-effort by contract — but now a failure the MOUNT can see and log.
    }
  }

  async function handleStepNext(newData: Record<string, unknown>) {
    setSaving(true);
    const merged = { ...stepData[currentStepId], ...newData };
    const nextStepData = { ...stepData, [currentStepId]: merged };
    setStepData(nextStepData);

    if (currentStepId === 'domain-verify' && newData.domainUrl) {
      void triggerAnalysis(newData.domainUrl as string);
    }

    await persistStep(currentStepId, merged);
    setSaving(false);

    if (stepIndex < journey.steps.length - 1) {
      setStepIndex((i) => i + 1);
    } else {
      complete(nextStepData);
    }
  }

  function handleBack() {
    if (stepIndex > 0) setStepIndex((i) => i - 1);
  }

  function goToStep(stepId: ObwStepId) {
    const idx = journey.steps.indexOf(stepId);
    if (idx >= 0) setStepIndex(idx);
  }

  function handleSkip() {
    if (stepIndex < journey.steps.length - 1) {
      setStepIndex((i) => i + 1);
    } else {
      complete(stepData);
    }
  }

  // L1/L2/L5 — the engine never routes and never sniffs journeyId. Exit discards
  // in-flight work for an account-less journey (everything is in memory); both
  // paths use the in-app confirm panel.
  const exitDiscardsProgress = journey.exitPolicy === 'rollback' || !accountId;

  function handleClose() {
    if (exitDiscardsProgress) {
      setConfirmingExit(true);
      return;
    }
    onExit('close');
  }

  function confirmExit() {
    setConfirmingExit(false);
    onExit(journey.exitPolicy === 'rollback' ? 'rollback' : 'close');
  }

  if (fatalError) {
    return (
      <div className="obw-overlay" role="dialog" aria-modal="true" aria-label="Setup error">
        <div className="obw-modal">
          <div className="obw-body">
            <div className="portal-wizard-error" role="alert">
              <span className="portal-wizard-error-code">Error</span>
              {fatalError}
            </div>
            <button type="button" className="obw-btn-secondary" onClick={() => setFatalError('')}>
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isRequired = journey.required.includes(currentStepId);
  const mergedData = getStepData();

  const stepProps: StepProps = {
    journeyId,
    accountId,
    domainToken,
    actor,
    isRequired,
    data: mergedData,
    analysisResult,
    nextStepId: journey.steps[stepIndex + 1],
    onNext: (d) => void handleStepNext(d),
    onJumpToStep: goToStep,
    requestExit: () => onExit('close'),
    residency,
  };

  function renderStep() {
    switch (currentStepId) {
      case 'bow-welcome':
        return <BowWelcomeStep {...stepProps} />;
      case 'residency':
        return <StepResidency {...stepProps} />;
      case 'welcome':
        return <StepWelcome {...stepProps} />;
      case 'domain-verify':
        return <StepDomainVerify {...stepProps} />;
      case 'subscribe':
        return <StepSubscribe {...stepProps} />;
      case 'payment':
        return <StepPayment {...stepProps} />;
      case 'profile':
        return <StepProfile {...stepProps} />;
      case 'persona':
        return <StepPersona {...stepProps} analysisResult={analysisResult} />;
      case 'bow-complete':
        return <BowCompleteStep {...stepProps} />;
      case 'sow-complete':
        return <SowCompleteStep {...stepProps} />;
      case 'b2s-complete':
        return <B2sCompleteStep {...stepProps} />;
      // ADR-076 Option 2 — admin-assisted partner-apply (POW).
      case 'pa-pick-subscriber':
        return <PartnerPickSubscriberStep {...stepProps} />;
      case 'pa-welcome':
        return <PartnerWelcomeStep {...stepProps} />;
      case 'pa-terms':
        return <PartnerTermsStep {...stepProps} />;
      case 'pa-prefill':
        return <PartnerPrefillConfirmStep {...stepProps} />;
      case 'pa-business':
        return <PartnerBusinessDetailStep {...stepProps} />;
      case 'pa-submit':
        return <PartnerSubmitStep {...stepProps} />;
      case 'pa-status':
        return <PartnerStatusStep {...stepProps} />;
      default:
        return <div>Unknown step: {currentStepId as string}</div>;
    }
  }

  const totalSteps = journey.steps.length;
  const canSkip = !isRequired && stepIndex < totalSteps - 1;

  return (
    <div
      className="obw-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Onboarding — step ${stepIndex + 1} of ${totalSteps}`}
    >
      <div className="obw-modal">
        <button type="button" className="obw-close" onClick={handleClose} aria-label="Close">
          ×
        </button>

        <div className="obw-dots" role="presentation">
          {journey.steps.map((stepId, i) => {
            let cls = 'obw-dot';
            if (i < stepIndex) cls += ' done';
            else if (i === stepIndex) cls += ' active';
            return <span key={stepId} className={cls} title={MASTER_STEP_LABELS[stepId]} />;
          })}
        </div>
        <p className="obw-progress-caption" aria-live="polite">
          Step {stepIndex + 1} of {totalSteps} — {MASTER_STEP_LABELS[currentStepId]}
        </p>

        {analysisLoading && (
          <div className="obw-analysis-loading" aria-live="polite">
            Analysing the website…
          </div>
        )}

        <div className="obw-body">
          {saving && <div className="sr-only" aria-live="polite">Saving…</div>}
          {renderStep()}
        </div>

        <div className="obw-footer">
          <div className="obw-footer-left">
            {stepIndex > 0 && (
              <button type="button" className="obw-btn-secondary" onClick={handleBack}>
                ← Back
              </button>
            )}
          </div>
          <div className="obw-footer-right">
            {canSkip && (
              <button type="button" className="obw-skip" onClick={handleSkip}>
                Skip — I'll do this later
              </button>
            )}
          </div>
        </div>

        {confirmingExit && (
          <div className="obw-confirm-scrim" role="alertdialog" aria-modal="true" aria-label="Confirm exit">
            <div className="obw-confirm-panel">
              <h3 className="obw-confirm-title">Exit beta onboarding?</h3>
              <p className="obw-confirm-body">Any details entered will be discarded.</p>
              <div className="obw-confirm-actions">
                <button type="button" className="obw-btn-secondary" onClick={() => setConfirmingExit(false)}>
                  Keep editing
                </button>
                <button type="button" className="obw-btn-danger" onClick={confirmExit}>
                  Exit & discard
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
